export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Plaatsing van een afbeelding in KC's ontwerp-JSON:
 * `x`/`y` = linkerbovenhoek, `size` = de LANGSTE zijde (breedte bij liggend, hoogte bij staand),
 * `aspect` = hoogte / breedte. Afgeleid uit HG-ontwerpen: een staande placeholder
 * (geboortekaart-met-foto-en-optioneel-kalkkaartje) past alleen op zijn kader met deze lezing.
 */
export interface Placement {
  x: number;
  y: number;
  size: number;
  aspect: number;
}

export function boundsOf(points: Point[]): Rect {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

/**
 * Contain-fit: de hele afbeelding past in het bestaande kader en blijft gecentreerd.
 * Wijkt de verhouding af, dan blijft er lege ruimte in het kader. Er wordt niets afgesneden
 * en het clip-pad van het kader verandert niet.
 */
export function containFit(frame: Rect, imgWidth: number, imgHeight: number): Placement {
  const aspect = round6(imgHeight / imgWidth);
  const frameAspect = frame.h / frame.w;
  let width: number;
  let height: number;
  if (aspect > frameAspect) {
    height = frame.h;
    width = height / aspect;
  } else {
    width = frame.w;
    height = width * aspect;
  }
  return rounded({
    x: frame.x + (frame.w - width) / 2,
    y: frame.y + (frame.h - height) / 2,
    size: Math.max(width, height),
    aspect,
  });
}

/**
 * Cover-fit: de afbeelding vult het hele kader en blijft gecentreerd.
 * Wijkt de verhouding af, dan valt de rest buiten het kader; het clip-pad snijdt dat weg.
 * Zo plaatst KC de bestaande foto's, en zo toont de editor ze.
 */
export function coverFit(frame: Rect, imgWidth: number, imgHeight: number): Placement {
  const aspect = round6(imgHeight / imgWidth);
  const frameAspect = frame.h / frame.w;
  let width: number;
  let height: number;
  if (aspect > frameAspect) {
    width = frame.w;
    height = width * aspect;
  } else {
    height = frame.h;
    width = height / aspect;
  }
  return rounded({
    x: frame.x + (frame.w - width) / 2,
    y: frame.y + (frame.h - height) / 2,
    size: Math.max(width, height),
    aspect,
  });
}

/** Breedte en hoogte van een plaatsing (KC's `size` is de langste zijde). */
export function placedSize(p: Placement): { width: number; height: number } {
  return p.aspect > 1 ? { width: p.size / p.aspect, height: p.size } : { width: p.size, height: p.size * p.aspect };
}

function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

function rounded(p: Placement): Placement {
  return { x: round6(p.x), y: round6(p.y), size: round6(p.size), aspect: p.aspect };
}
